import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "../contexts/ToastContext"; // Import useToast
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  OutlinedInput,
  Grid,
  AppBar,
  Toolbar,
  Chip,
  Stack,
  CircularProgress,
  alpha,
} from "@mui/material";
import {
  ArrowBackTwoTone as ArrowBack,
  SearchTwoTone as Search,
  FilterListTwoTone as FilterList,
  RefreshTwoTone as Refresh,
  CloseTwoTone as Close,
} from "@mui/icons-material";
import { useAuth } from "../contexts/AuthContext";
import ProfileGrid from "../components/profile/ProfileGrid";
import CustomPagination from "../components/ui/CustomPagination";
import FilterSection from "../components/search/FilterSection";
import AgeRangeFilter from "../components/search/AgeRangeFilter";

import apiEndpoints from "../apiConfig";

const PROFILES_PER_PAGE = 8;

const defaultFilters = {
  ageRange: [18, 60],
  location: "all",
  profession: "all",
  religion: "all",
  heightRange: [150, 185],
  maritalStatus: "all",
};

const allowedMaritalStatus = ["Married", "Unmarried", "Divorced", "Widow"];
const excludedReligions = ["Sikkim", "Buddhist", "Jain"];

const FilterContent = ({
  filters,
  searchQuery,
  setSearchQuery,
  appliedQuerySetter,
  updateFilter,
  resetFilters,
  isMobile,
  cityOptions,
  professionOptions,
  religionOptions,
  maritalStatusOptions,
  onClose,
}) => (
  <Box
    sx={{
      p: { xs: 2.5, sm: 3 },
      width: isMobile ? "100vw" : "100%",
      maxWidth: 400,
    }}
  >
    {/* Header for mobile drawer */}
    {isMobile && (
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Filters
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </Box>
    )}

    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 3,
      }}
    >
      {!isMobile && (
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Filters
        </Typography>
      )}
      <Button
        startIcon={<Refresh />}
        onClick={resetFilters}
        size="small"
        sx={{
          color: "text.primary",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          "&:hover": {
            backgroundColor: "rgba(212, 175, 55, 0.1)", // Gold tint
            borderColor: "secondary.main",
          },
          fontSize: "0.8rem",
          textTransform: "none",
        }}
      >
        Reset All
      </Button>
    </Box>

    {/* Search */}
    <FilterSection title="Search" icon={<Search />}>
      <Box sx={{ display: "flex", gap: 1 }}>
        <TextField
          fullWidth
          placeholder="Enter name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          InputProps={{
            style: { fontSize: "0.9rem" },
          }}
        />
        <Button
          variant="contained"
          color="secondary"
          onClick={() => appliedQuerySetter(searchQuery)}
          sx={{
            minWidth: "auto",
            px: 1.5,
            borderRadius: 2,
          }}
        >
          <Search fontSize="small" />
        </Button>
      </Box>
    </FilterSection>

    {/* Age */}
    <FilterSection title="Age">
      <AgeRangeFilter
        value={filters.ageRange}
        onChange={(value) => updateFilter("ageRange", value)}
        isMobile={isMobile}
      />
    </FilterSection>

    {/* Location */}
    <FilterSection title="Location">
      <FormControl fullWidth size="small">
        <InputLabel>Select location</InputLabel>
        <Select
          value={filters.location}
          onChange={(e) => updateFilter("location", e.target.value)}
          input={<OutlinedInput label="Select location" />}
        >
          {cityOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </FilterSection>

    {/* Profession */}
    <FilterSection title="Profession">
      <FormControl fullWidth size="small">
        <InputLabel>Select profession</InputLabel>
        <Select
          value={filters.profession}
          onChange={(e) => updateFilter("profession", e.target.value)}
          input={<OutlinedInput label="Select profession" />}
        >
          {professionOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </FilterSection>

    {/* Religion */}
    <FilterSection title="Religion">
      <FormControl fullWidth size="small">
        <InputLabel>Select religion</InputLabel>
        <Select
          value={filters.religion}
          onChange={(e) => updateFilter("religion", e.target.value)}
          input={<OutlinedInput label="Select religion" />}
        >
          {religionOptions
            .filter((r) => !excludedReligions.includes(r.value))
            .map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </FilterSection>

    {/* Marital Status */}
    <FilterSection title="Marital Status">
      <FormControl fullWidth size="small">
        <InputLabel>Select status</InputLabel>
        <Select
          value={filters.maritalStatus}
          onChange={(e) => updateFilter("maritalStatus", e.target.value)}
          input={<OutlinedInput label="Select status" />}
        >
          {maritalStatusOptions.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </FilterSection>
  </Box>
);

const MemoizedFilterContent = React.memo(FilterContent);

// Active filters display component
const ActiveFilters = ({ filters, defaultFilters, onRemoveFilter }) => {
  const activeFilters = [];

  if (
    filters.ageRange[0] !== defaultFilters.ageRange[0] ||
    filters.ageRange[1] !== defaultFilters.ageRange[1]
  ) {
    activeFilters.push({
      key: "age",
      label: `Age: ${filters.ageRange[0]}-${filters.ageRange[1]}`,
    });
  }

  if (filters.location !== defaultFilters.location) {
    activeFilters.push({
      key: "location",
      label: `Location: ${filters.location}`,
    });
  }

  if (filters.profession !== defaultFilters.profession) {
    activeFilters.push({
      key: "profession",
      label: `Profession: ${filters.profession}`,
    });
  }

  if (filters.religion !== defaultFilters.religion) {
    activeFilters.push({
      key: "religion",
      label: `Religion: ${filters.religion}`,
    });
  }

  if (filters.maritalStatus !== defaultFilters.maritalStatus) {
    activeFilters.push({
      key: "maritalStatus",
      label: `Status: ${filters.maritalStatus}`,
    });
  }

  if (activeFilters.length === 0) return null;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="body2"
        sx={{ mb: 1.5, fontWeight: 600, color: "text.secondary" }}
      >
        Active Filters:
      </Typography>
      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        {activeFilters.map((filter) => (
          <Chip
            key={filter.key}
            label={filter.label}
            size="small"
            onDelete={() => onRemoveFilter(filter.key)}
            sx={{ mb: 1, borderRadius: 1.5, fontWeight: 500 }}
            color="secondary"
            variant="outlined"
          />
        ))}
      </Stack>
    </Box>
  );
};

function SearchPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));

  const [allProfiles, setAllProfiles] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState(defaultFilters);
  const [searchQuery, setSearchQuery] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const fetchProfiles = async () => {
      try {
        setLoading(true);
        const res = await fetch(
          `${apiEndpoints.getProfile}?user_id=${user.id}&page=1&limit=1000`,
        );
        const data = await res.json();
        if (data.success) {
          setAllProfiles(data.profiles);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchProfiles();
  }, [user]);

  // Dynamic options
  const cityOptions = useMemo(() => {
    const unique = [...new Set(allProfiles.map((p) => p.city).filter(Boolean))];
    return [
      { value: "all", label: "All Locations" },
      ...unique.map((c) => ({ value: c, label: c })),
    ];
  }, [allProfiles]);

  const professionOptions = useMemo(() => {
    const unique = [
      ...new Set(allProfiles.map((p) => p.profession).filter(Boolean)),
    ];
    return [
      { value: "all", label: "All Professions" },
      ...unique.map((p) => ({ value: p, label: p })),
    ];
  }, [allProfiles]);

  const religionOptions = useMemo(() => {
    const unique = [
      ...new Set(allProfiles.map((p) => p.religion).filter(Boolean)),
    ].filter((r) => !excludedReligions.includes(r));
    return [
      { value: "all", label: "All Religions" },
      ...unique.map((r) => ({ value: r, label: r })),
    ];
  }, [allProfiles]);

  const maritalStatusOptions = useMemo(() => {
    return [
      { value: "all", label: "All Status" },
      ...allowedMaritalStatus.map((m) => ({ value: m, label: m })),
    ];
  }, []);

  // Filtering
  const filteredProfiles = useMemo(() => {
    return allProfiles.filter((p) => {
      if (
        appliedQuery &&
        !p.name.toLowerCase().includes(appliedQuery.toLowerCase())
      )
        return false;
      if (
        typeof p.age === "number" &&
        (p.age < filters.ageRange[0] || p.age > filters.ageRange[1])
      )
        return false;

      if (
        typeof p.height === "number" &&
        (p.height < filters.heightRange[0] || p.height > filters.heightRange[1])
      )
        return false;

      if (filters.location !== "all" && p.city !== filters.location)
        return false;
      if (filters.profession !== "all" && p.profession !== filters.profession)
        return false;
      if (filters.religion !== "all" && p.religion !== filters.religion)
        return false;
      if (
        filters.maritalStatus !== "all" &&
        p.marital_status !== filters.maritalStatus
      )
        return false;
      return true;
    });
  }, [allProfiles, filters, appliedQuery]);

  const totalProfiles = filteredProfiles.length;
  const totalPages = Math.ceil(totalProfiles / PROFILES_PER_PAGE);
  const paginatedProfiles = filteredProfiles.slice(
    (currentPage - 1) * PROFILES_PER_PAGE,
    currentPage * PROFILES_PER_PAGE,
  );

  const handleViewProfile = (profileId) => navigate(`/profile/${profileId}`);
  const handleAddToWishlist = (profileId) =>
    console.log("Added to wishlist:", profileId);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const resetFilters = () => {
    setFilters(defaultFilters);
    setSearchQuery("");
    setAppliedQuery("");
    setCurrentPage(1);
    if (isMobile || isTablet) {
      setShowFilters(false);
    }
  };

  const { showToast } = useToast();
  const prevProfileCount = useRef(0);
  const [enquiryStatuses, setEnquiryStatuses] = useState({});

  // Fetch Enquiry Statuses
  useEffect(() => {
    if (user) {
      fetch(`${apiEndpoints.checkEnquiryStatus}?requester_id=${user.id}`)
        .then((res) => res.json())
        .then((data) => {
          if (Array.isArray(data)) {
            const statusMap = {};
            data.forEach((item) => {
              statusMap[item.receiver_id] = item.status;
            });
            setEnquiryStatuses(statusMap);
          }
        })
        .catch((err) => console.error("Error fetching enquiry statuses:", err));
    }
  }, [user]);

  const handleSendEnquiry = (receiverId) => {
    fetch(apiEndpoints.sendEnquiry, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        requester_id: user.user_id,
        receiver_id: receiverId,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        if (data.status === "success") {
          showToast("Enquiry sent", "success");
          setEnquiryStatuses((prev) => ({
            ...prev,
            [receiverId]: "pending",
          }));
        } else if (data.status === "exists") {
          showToast("Enquiry already sent", "info");
          setEnquiryStatuses((prev) => ({
            ...prev,
            [receiverId]: "pending",
          }));
        } else if (data.status === "no_credit") {
          showToast("Upgrade your plan, credits finished", "error");
        } else {
          showToast(data.message || "Failed to send enquiry", "error");
        }
      })
      .catch((err) => {
        console.error("Error sending enquiry:", err);
        showToast("Network error", "error");
      });
  };

  // Merge profiles with their enquiry status
  const profilesWithStatus = paginatedProfiles.map((p) => ({
    ...p,
    enquiryStatus: enquiryStatuses[p.id] || null,
    onSendEnquiry: handleSendEnquiry,
  }));

  // Toast for "No profiles found"
  useEffect(() => {
    if (!loading) {
      if (filteredProfiles.length === 0 && prevProfileCount.current > 0) {
        showToast("No profiles found", "info");
      }
      prevProfileCount.current = filteredProfiles.length;
    }
  }, [filteredProfiles.length, loading]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
    showToast("Filters applied", "success");
  };

  const handleAppliedQuery = (val) => {
    setAppliedQuery(val);
    showToast("Filters applied", "success");
  };

  const removeFilter = (filterKey) => {
    switch (filterKey) {
      case "age":
        setFilters((prev) => ({ ...prev, ageRange: defaultFilters.ageRange }));
        break;
      case "location":
        setFilters((prev) => ({ ...prev, location: defaultFilters.location }));
        break;
      case "profession":
        setFilters((prev) => ({
          ...prev,
          profession: defaultFilters.profession,
        }));
        break;
      case "religion":
        setFilters((prev) => ({ ...prev, religion: defaultFilters.religion }));
        break;
      case "maritalStatus":
        setFilters((prev) => ({
          ...prev,
          maritalStatus: defaultFilters.maritalStatus,
        }));
        break;
      default:
        break;
    }
  };

  const handleCloseFilters = () => {
    setShowFilters(false);
  };

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "background.default" }}>
      <Container
        maxWidth="xl"
        sx={{
          display: "flex",
          flexDirection: "column",
          py: { xs: 2.5, sm: 3 },
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Grid container spacing={4} width={"100%"}>
          {/* Filters Sidebar - Desktop */}
          {!isTablet && (
            <Grid item xs={12} md={3} width={"28%"}>
              <Paper
                elevation={0}
                sx={{
                  position: "sticky",
                  top: 100,
                  maxHeight: "calc(100vh - 120px)",
                  overflow: "auto",
                  borderRadius: 3,
                  border: "1px solid",
                  borderColor: "divider",
                  bgcolor: "background.paper", // Clean look
                }}
              >
                <MemoizedFilterContent
                  filters={filters}
                  searchQuery={searchQuery}
                  setSearchQuery={setSearchQuery}
                  appliedQuerySetter={handleAppliedQuery}
                  updateFilter={updateFilter}
                  resetFilters={resetFilters}
                  isMobile={isMobile}
                  cityOptions={cityOptions}
                  professionOptions={professionOptions}
                  religionOptions={religionOptions}
                  maritalStatusOptions={maritalStatusOptions}
                />
              </Paper>
            </Grid>
          )}

          {/* Main Content */}
          <Grid item width={{ xs: "100%", md: "68%", lg: "68%" }}>
            {/* Results Header */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 2,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    variant="h5"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      fontSize: { xs: "1.5rem", sm: "1.75rem" },
                      mb: 0.5,
                      color: "primary.main",
                    }}
                  >
                    {appliedQuery
                      ? `Search: "${appliedQuery}"`
                      : "Discover Profiles"}
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
                  >
                    Found {totalProfiles}{" "}
                    {totalProfiles === 1 ? "match" : "matches"} for you
                  </Typography>
                </Box>

                {/* Mobile filter indicator */}
                {(isMobile || isTablet) && totalProfiles > 0 && (
                  <Button
                    startIcon={<FilterList />}
                    variant="outlined"
                    onClick={() => setShowFilters(true)}
                    color="secondary"
                    sx={{ borderRadius: 4 }}
                  >
                    Filters (
                    {
                      Object.keys(filters).filter(
                        (key) => filters[key] !== defaultFilters[key],
                      ).length
                    }
                    )
                  </Button>
                )}
              </Box>

              {/* Active Filters */}
              <ActiveFilters
                filters={filters}
                defaultFilters={defaultFilters}
                onRemoveFilter={removeFilter}
              />
            </Box>

            {/* Loading State */}
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress color="secondary" />
              </Box>
            ) : (
              <>
                {/* Profile Grid */}
                {totalProfiles > 0 ? (
                  <Box sx={{ width: "100%" }}>
                    <ProfileGrid
                      profiles={profilesWithStatus}
                      onViewProfile={handleViewProfile}
                      onAddToWishlist={handleAddToWishlist}
                      pageType="search"
                    />

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <Box
                        sx={{
                          mt: 5,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <CustomPagination
                          currentPage={currentPage}
                          totalPages={totalPages}
                          onPageChange={handlePageChange}
                        />
                      </Box>
                    )}
                  </Box>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 6,
                      textAlign: "center",
                      borderRadius: 4,
                      bgcolor: alpha(theme.palette.background.paper, 0.6),
                      border: "1px dashed",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="text.primary"
                      gutterBottom
                      fontWeight={600}
                    >
                      No profiles found
                    </Typography>
                    <Typography
                      variant="body1"
                      color="text.secondary"
                      sx={{ mb: 3 }}
                    >
                      Try adjusting your filters or search terms to find more
                      results.
                    </Typography>
                    <Button
                      variant="contained"
                      color="secondary"
                      onClick={resetFilters}
                      sx={{ color: "black" }}
                    >
                      Clear All Filters
                    </Button>
                  </Paper>
                )}
              </>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Mobile/Tablet Filter Drawer */}
      {(isMobile || isTablet) && (
        <Drawer
          anchor="left"
          open={showFilters}
          onClose={handleCloseFilters}
          sx={{
            "& .MuiDrawer-paper": {
              width: { xs: "100%", sm: 350 },
              maxWidth: "100%",
              borderTopRightRadius: 16,
              borderBottomRightRadius: 16,
            },
          }}
        >
          <MemoizedFilterContent
            filters={filters}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            appliedQuerySetter={(val) => {
              setAppliedQuery(val);
              setShowFilters(false);
            }}
            updateFilter={updateFilter}
            resetFilters={resetFilters}
            isMobile={isMobile}
            cityOptions={cityOptions}
            professionOptions={professionOptions}
            religionOptions={religionOptions}
            maritalStatusOptions={maritalStatusOptions}
            onClose={handleCloseFilters}
          />
        </Drawer>
      )}
    </Box>
  );
}

export default SearchPage;
